// Holding a size for a customer (server cwr_fsh_hold: createFashionHold,
// getFashionHolds, closeFashionHold). A hold keeps pieces back from the store's
// free stock for up to MAX_HOLD_DAYS; it ends sold (SLD, with the bill's
// orderId), released (REL) or expired (EXP, worked out by the server).
//
// When a held item goes on a bill, the till remembers { holdId, sOrderID,
// productId } in localStorage. Once that bill is saved (db.orders.get finds it)
// with the item still sold on it, the hold is closed as sold. A bill that never
// completes, is discarded (Core saves those with every qty zeroed) or lost the
// item leaves the hold open until it expires or is released.
import dayjs from "dayjs";

export const MAX_HOLD_DAYS = 7;
const PENDING_KEY = "fashionHoldsOnBill";

const clean = (s) => String(s ?? "").replace(/["\\]/g, "").replace(/[\r\n]+/g, " ").trim();
const str = (s) => `"${clean(s)}"`;

export function buildHoldsQuery(bunitId) {
  return {
    query: `query { getFashionHolds(bunitId: ${str(bunitId)}) {
      holdId documentNo productId productName qty customerId customerName mobileNo heldUntil status note created
    }}`,
  };
}

export function buildCreateHoldMutation(h) {
  const fields = [
    `bunitId: ${str(h.bunitId)}`,
    `productId: ${str(h.productId)}`,
    `qty: ${Number(h.qty)}`,
    `heldUntil: ${str(h.heldUntil)}`,
    h.customerId ? `customerId: ${str(h.customerId)}` : null,
    h.customerName ? `customerName: ${str(h.customerName)}` : null,
    h.mobileNo ? `mobileNo: ${str(h.mobileNo)}` : null,
    h.note ? `note: ${str(h.note)}` : null,
  ].filter(Boolean);
  return { query: `mutation { createFashionHold(input: { ${fields.join(", ")} }) { status message recordId documentNo } }` };
}

export function buildCloseHoldMutation(holdId, outcome, orderId) {
  const order = orderId ? `, orderId: ${str(orderId)}` : "";
  return { query: `mutation { closeFashionHold(holdId: ${str(holdId)}, outcome: ${str(outcome)}${order}) { status message recordId documentNo } }` };
}

export function readHolds(response) {
  if (!response || response?.data?.errors?.length) return null;
  const rows = response?.data?.data?.getFashionHolds;
  if (!Array.isArray(rows)) return null;
  return rows.map((r) => ({
    id: r.holdId,
    documentNo: r.documentNo || "",
    productId: r.productId,
    productName: r.productName || "",
    qty: Number(r.qty) || 0,
    customerId: r.customerId || null,
    customerName: r.customerName || "",
    mobileNo: r.mobileNo || "",
    heldUntil: r.heldUntil || "",
    status: r.status,
    note: r.note || "",
  }));
}

// A CWMessage mutation result → { ok, message, id, documentNo }.
export function readMessage(response, field, fallback) {
  const errors = response?.data?.errors;
  if (!response) return { ok: false, message: fallback };
  if (errors?.length) return { ok: false, message: errors[0]?.message || fallback };
  const m = response?.data?.data?.[field];
  if (!m) return { ok: false, message: fallback };
  return { ok: m.status === "Success", message: m.message || (m.status === "Success" ? "" : fallback), id: m.recordId || null, documentNo: m.documentNo || "" };
}

// Default end of a hold: two days from now, at the close of the day.
export const defaultHeldUntil = (now = new Date()) => dayjs(now).add(2, "day").hour(20).minute(0).format("YYYY-MM-DDTHH:mm");

// Form values → { hold } ready for buildCreateHoldMutation, or { error }.
export function validateHoldDraft(draft, now = new Date()) {
  const qty = Number(draft?.qty);
  if (!Number.isInteger(qty) || qty < 1) return { error: "Hold a whole number of pieces, at least 1." };
  const name = clean(draft?.customerName);
  const mobile = clean(draft?.mobileNo);
  if (!name && !mobile) return { error: "Add the customer's name or mobile so the hold can be found." };
  if (mobile && !/^\+?[0-9 ]{6,15}$/.test(mobile)) return { error: "Enter the mobile number as digits." };
  const until = dayjs(draft?.heldUntil);
  if (!draft?.heldUntil || !until.isValid()) return { error: "Choose when the hold ends." };
  if (!until.isAfter(dayjs(now))) return { error: "The hold must end in the future." };
  if (until.isAfter(dayjs(now).add(MAX_HOLD_DAYS, "day"))) return { error: `A size can be held for at most ${MAX_HOLD_DAYS} days.` };
  return {
    hold: {
      productId: draft.productId,
      qty,
      customerId: draft.customerId || null,
      customerName: name,
      mobileNo: mobile,
      heldUntil: until.format("YYYY-MM-DDTHH:mm"),
      note: clean(draft?.note),
    },
  };
}

// Open holds matching a search: hold no., product, customer name or mobile.
export function searchHolds(holds, term) {
  const q = String(term || "").trim().toLowerCase();
  if (!q) return holds || [];
  return (holds || []).filter((h) => [h.documentNo, h.productName, h.customerName, h.mobileNo].some((v) => String(v || "").toLowerCase().includes(q)));
}

// Pieces of a product held in this store right now.
export const heldQty = (holds, productId) => (holds || []).filter((h) => h.productId === productId && h.status === "HLD").reduce((n, h) => n + h.qty, 0);

export function loadPending(bunitId, storage = globalThis.localStorage) {
  try {
    const rows = JSON.parse(storage?.getItem(`${PENDING_KEY}:${bunitId}`) || "[]");
    return Array.isArray(rows) ? rows.filter((r) => r?.holdId && r?.sOrderID) : [];
  } catch {
    return [];
  }
}

function savePending(bunitId, rows, storage) {
  try {
    storage?.setItem(`${PENDING_KEY}:${bunitId}`, JSON.stringify(rows));
  } catch {
    // without the note the hold just expires instead of closing as sold
  }
}

export function rememberHoldOnBill(bunitId, entry, storage = globalThis.localStorage) {
  const rows = loadPending(bunitId, storage).filter((r) => r.holdId !== entry.holdId);
  savePending(bunitId, [...rows, entry], storage);
}

export function forgetHolds(bunitId, holdIds, storage = globalThis.localStorage) {
  const drop = new Set(holdIds);
  savePending(bunitId, loadPending(bunitId, storage).filter((r) => !drop.has(r.holdId)), storage);
}

// The saved bill still sells this product (a discarded bill keeps its lines at qty 0).
export const billSells = (order, productId) =>
  (order?.items || []).some((i) => i?.productId === productId && Number(i.weight) > 0 && i.isReturn !== true && i.isReturn !== "Y");

// The server has the bill (isSynced 1). Until then the bill can still be
// refused, so the hold stays open.
const billBooked = (order) => order?.isSynced === 1 || order?.isSynced === "1";
// The server answers these for a hold that is no longer open: retrying won't help.
const FINAL_REPLY = /already closed|not found/i;

// Holds on bills the server has booked get closed as sold (or dropped when the
// bill doesn't sell the item, leaving the hold open); holds whose end has
// passed are dropped (the server expires them). Returns the hold ids handled.
// getOrder(sOrderID) → saved order or null; close(holdId, orderId) → { ok, message }.
export async function settlePending(pending, { currentOrderId, getOrder, close, now = new Date() }) {
  const done = [];
  for (const p of pending) {
    if (p.sOrderID === currentOrderId) continue;
    let order = null;
    try {
      order = await getOrder(p.sOrderID);
    } catch {
      order = null;
    }
    const ended = Boolean(p.heldUntil) && dayjs(p.heldUntil).isBefore(dayjs(now));
    if (order && p.productId && !billSells(order, p.productId)) {
      done.push(p.holdId);
    } else if (billBooked(order)) {
      const res = await close(p.holdId, p.sOrderID);
      if (res?.ok || FINAL_REPLY.test(res?.message || "") || ended) done.push(p.holdId);
    } else if (ended) {
      done.push(p.holdId);
    }
  }
  return done;
}
